import * as React from 'react';
import { Controller, ControllerProps, FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { cn } from '@/lib/utils';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

interface TextFieldProps {
  name: string;
  label?: string;
  placeholder?: string;
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url';
  disabled?: boolean;
  required?: boolean;
  readOnly?: boolean;
  className?: string;
  rows?: number;
  maxLength?: number;
  min?: number;
  max?: number;
}

export const TextField = React.forwardRef<HTMLInputElement, TextFieldProps>(
  (
    {
      name,
      label,
      placeholder,
      type = 'text',
      disabled = false,
      required = false,
      readOnly = false,
      className,
      maxLength,
      min,
      max,
    },
    ref
  ) => {
    const { control, formState: { errors } } = useFormContext();
    const error = errors[name];

    return (
      <div className={cn('space-y-2', className)}>
        {label && (
          <Label htmlFor={name}>
            {label}
            {required && <span className="text-destructive ml-1">*</span>}
          </Label>
        )}
        <Controller
          name={name}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              id={name}
              type={type}
              placeholder={placeholder}
              disabled={disabled}
              readOnly={readOnly}
              required={required}
              maxLength={maxLength}
              min={min}
              max={max}
              className={cn(error && 'border-destructive')}
              onChange={(e) => {
                let value: string | number = e.target.value;
                if (type === 'number' && value) {
                  value = Number(value);
                }
                field.onChange(value);
              }}
            />
          )}
        />
        {error && (
          <p className="text-sm text-destructive">{String(error.message)}</p>
        )}
      </div>
    );
  }
);

TextField.displayName = 'TextField';

export default TextField;
